import { useState } from 'react';
import { Link } from 'react-router-dom';
import { auth } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Cpu, ArrowLeft, Check, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export default function ResetPassword() {
  const [step, setStep] = useState('request'); // request | confirm | done
  const [email, setEmail] = useState('');
  const [token, setToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleRequest = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await auth.requestReset({ email });
      toast.success('Reset instructions sent');
      if (res.data.dev_token) setToken(res.data.dev_token);
      setStep('confirm');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Request failed');
    }
    setLoading(false);
  };

  const handleConfirm = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) { toast.error('Min 6 characters'); return; }
    setLoading(true);
    try {
      await auth.confirmReset({ token, new_password: newPassword });
      toast.success('Password reset! You can now log in.');
      setStep('done');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reset failed');
    }
    setLoading(false);
  };

  return (
    <div className="light-page min-h-screen flex items-center justify-center p-4" style={{background: `linear-gradient(to bottom right, #f8fafc, color-mix(in srgb, var(--brand-primary) 4%, white))`}}>
      <Card data-testid="reset-card" className="w-full max-w-md border-slate-200 bg-white shadow-xl shadow-slate-200/50">
        <CardHeader className="text-center space-y-3">
          <img src="/tensornex-logo.png" alt="Tensornex" className="mx-auto w-14 h-14 object-contain" />
          <CardTitle className="text-2xl text-slate-900" style={{fontFamily: "'Plus Jakarta Sans', sans-serif"}}>
            {step === 'done' ? 'Password Reset' : step === 'confirm' ? 'Enter New Password' : 'Reset Password'}
          </CardTitle>
          <CardDescription>
            {step === 'done' ? 'Your password has been changed.' : step === 'confirm' ? 'Enter the token and your new password.' : 'Enter your email to receive a reset token.'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {step === 'request' && (
            <form onSubmit={handleRequest} className="space-y-4">
              <div className="space-y-2">
                <Label>Email</Label>
                <Input data-testid="reset-email" type="email" placeholder="you@company.com" value={email} onChange={e => setEmail(e.target.value)} required />
              </div>
              <Button data-testid="reset-submit" type="submit" className="w-full brand-btn h-10 text-sm" disabled={loading}>
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Send Reset Token'}
              </Button>
            </form>
          )}
          {step === 'confirm' && (
            <form onSubmit={handleConfirm} className="space-y-4">
              <div className="space-y-2">
                <Label>Reset Token</Label>
                <Input data-testid="reset-token" placeholder="Paste your token" value={token} onChange={e => setToken(e.target.value)} required className="font-mono text-sm" />
              </div>
              <div className="space-y-2">
                <Label>New Password</Label>
                <Input data-testid="reset-new-password" type="password" placeholder="Min 6 characters" value={newPassword} onChange={e => setNewPassword(e.target.value)} required minLength={6} />
              </div>
              <Button data-testid="reset-confirm" type="submit" className="w-full brand-btn h-10 text-sm" disabled={loading}>
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Reset Password'}
              </Button>
            </form>
          )}
          {step === 'done' && (
            <div className="text-center space-y-4">
              <div className="mx-auto w-12 h-12 rounded-full bg-emerald-50 flex items-center justify-center">
                <Check className="w-6 h-6 text-emerald-600" />
              </div>
              <Link to="/login" className="inline-flex items-center gap-2 brand-link font-medium">
                Go to Login
              </Link>
            </div>
          )}
          {step !== 'done' && (
            <p className="text-center text-sm text-slate-500 mt-4">
              <Link to="/login" className="brand-link inline-flex items-center gap-1">
                <ArrowLeft className="w-3 h-3" /> Back to login
              </Link>
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
